import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, ContextualField, IValueViewModel, KeyValuePair, NameBuilderConfiguration, PropertyType } from '../../../models';
import * as Fields from '../../../common/helpers/contextual';
import { TableByGroupWidgetConfiguration } from '../tableByGroup.configuration';
import { INameBuilderService } from '../../../services';
import { sortByOptionsTableByGroup, toDictionary } from '../../../common';

const defaultSortBy = [{ key: '', value: 'Group name' }];

class TableByGroupContextualController implements angular.IComponentController {
    configuration: TableByGroupWidgetConfiguration;
    formController: angular.IFormController;
    isLoading: boolean;
    loadingMessage: string;
    periodReferences: { key: number; value: string }[];
    properties: ContextualField[];

    readonly cancel: () => void;
    readonly isBindable: boolean;
    readonly save: () => void;
    readonly viewModels: IValueViewModel[];

    private _originalConfiguration: TableByGroupWidgetConfiguration;

    static $inject = ['$scope', '$q', 'nameBuilderService'];

    constructor(
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _nameBuilderService: INameBuilderService
    ) {
    }

    $onInit() {
        this._originalConfiguration = angular.copy(this.configuration);
        this.initProperties();

        if (!this.configuration.groupTypes) {
            this.configuration.groupTypes = [];
        }

        this._$scope.$on('identifierAdded', (event, data) => {
            const res = data.group;
            if (res && res.type && (_.every(this.configuration.groupTypes, t => t !== res.type)
                && (this.configuration.groupGuidCollection && this.configuration.groupGuidCollection.indexOf(res.guid) < 0))) {
                this.configuration.groupTypes.push(res.type);
            }
        });

        if (this.configuration) {
            this._$scope.$watchCollection(
                () => this.configuration.series,
                (oldValue, newValue) => {
                    if (!!this.viewModels && !!this.properties) {
                        this.updateSortByOptions();
                    }
                }
            );
        }
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['configuration']) {
            this._originalConfiguration = angular.copy(this.configuration);
            if (this.properties) {
                const found = _.find(this.properties, p => p.propertyName === 'maxItemsProperty');
                if (found) {
                    found.hide = !this.configuration?.limit;
                }
                this.updateSortByOptions();
            }
        }
        if (changes['viewModels'] && this.properties && this.configuration) {
            this.updateSortByOptions();
        }
    }

    private initProperties() {
        if (this.configuration.alignedOn === undefined) {
            this.configuration.alignedOn = 0;
        }
        const aggregationModeField = angular.copy(Fields.AggregationMode);
        aggregationModeField.modelName = 'aggregationMode';
        aggregationModeField.extraData = {};

        // Sorting
        const sortBy = angular.copy(Fields.SortBy);
        // TODO
        sortBy.extraData = { options: this.getSortByOptions(this.configuration) };
        const showLimit = angular.copy(Fields.ShowLimit);
        const maxItemsProperty = angular.copy(Fields.MaxNumberOfItems);
        showLimit.onChange = (viewValue, extraArgs) => maxItemsProperty.hide = viewValue === 0;
        maxItemsProperty.hide = !this.configuration?.limit;

        const properties = [
            angular.copy(Fields.RowSpacingSelect),
            aggregationModeField,
            angular.copy(Fields.MinPeriod),
            angular.copy(Fields.MaxPeriod),
            angular.copy(Fields.PeriodReference),
            angular.copy(Fields.IsEndDateIncluded),
            {
                modelName: 'hasAggregationRow',
                propertyType: PropertyType.Boolean,
                label: 'Aggregation row',
                placeholder: 'Aggregation row',
                propertyName: 'hasAggregationRowProperty',
                required: false,
                iconText: 'Σ',
                addSeparatorAfter: true
            },
            {
                modelName: 'showGroupShortComment',
                propertyType: PropertyType.Boolean,
                label: 'Group short comment',
                placeholder: 'Group short comment',
                propertyName: 'showGroupShortCommentProperty',
                required: false,
                icon: ['fa-sitemap', 'fa-comment-o']
            },
            {
                modelName: 'showGroupLongComment',
                propertyType: PropertyType.Boolean,
                label: 'Group long comment',
                placeholder: 'Group long comment',
                propertyName: 'showGroupLongCommentProperty',
                required: false,
                icon: ['fa-sitemap', 'fa-commenting-o']
            },
            {
                modelName: 'hasLegend',
                propertyType: PropertyType.Boolean,
                label: 'Legend',
                placeholder: 'Legend',
                propertyName: 'hasLegendProperty',
                required: false,
                icon: 'fa-list'
            },
            {
                modelName: 'hasName',
                propertyType: PropertyType.Boolean,
                label: 'Identifier name',
                placeholder: 'Identifier name',
                propertyName: 'hasNameProperty',
                required: false,
                icon: 'fa-id-badge'
            },
            {
                modelName: 'hasShortComment',
                propertyType: PropertyType.Boolean,
                label: 'Short comment',
                placeholder: 'Short comment',
                propertyName: 'hasShortCommentProperty',
                required: false,
                icon: 'fa-comment-o'
            },
            {
                modelName: 'hasValueType',
                propertyType: PropertyType.Boolean,
                label: 'Value type',
                placeholder: 'Value type',
                propertyName: 'hasValueTypeProperty',
                required: false,
                icon: 'fa-neuter',
                iconText: 'V'
            },
            {
                modelName: 'hasUnits',
                propertyType: PropertyType.Boolean,
                label: 'Unit',
                placeholder: 'Unit',
                propertyName: 'hasUnitsProperty',
                required: false,
                icon: 'fa-balance-scale'
            },
            {
                modelName: 'hideFieldDescriptions',
                propertyType: PropertyType.Boolean,
                label: 'Hide field descriptions',
                placeholder: 'Hide field descriptions',
                propertyName: 'hideFieldDescriptionsProperty',
                required: false,
                icon: 'fa-eye-slash',
                addSeparatorAfter: true
            },
            angular.copy(Fields.GroupTypes),
            angular.copy(Fields.GroupNames),
            {
                modelName: 'isSearchByRelativePath',
                propertyType: PropertyType.Boolean,
                label: 'Search by relative path',
                placeholder: 'Search by relative path',
                propertyName: 'isSearchByRelativePathProperty',
                required: false,
                icon: ['fa-sitemap', 'fa-search']
            }
        ];
        if (this.isBindable) {
            properties.push(Object.assign({}, Fields.IsBoundToGroup, { disabled: !this.isBindable }));
        }
        const otherProperties = [
            {
                modelName: 'hasQuality',
                propertyType: PropertyType.Boolean,
                label: 'Quality',
                placeholder: 'Quality',
                propertyName: 'hasQualityProperty',
                required: false,
                icon: 'fa-check'
            },
            {
                modelName: 'hasAlarm',
                propertyType: PropertyType.Boolean,
                label: 'Alarms',
                placeholder: 'Alarms',
                propertyName: 'hasAlarmProperty',
                required: false,
                icon: 'fa-bell'
            },
            sortBy,
            angular.copy(Fields.SortOrder),
            showLimit,
            maxItemsProperty,
            angular.copy(Fields.PresentationUnit)
        ];
        this.properties = properties.concat(otherProperties);
    }

    onSave(): angular.IPromise<void> {
        return this._$q.when(this.save());
    }


    onCancel() {
        this.configuration = angular.copy(this._originalConfiguration);
        this.cancel();
    }

    private updateSortByOptions() {
        const found = _.find(this.properties, p => p.propertyName === 'sortByProperty');
        if (found) {
            const sortByOptions = this.getSortByOptions(this.configuration);
            if (sortByOptions.map(d => d.key).indexOf(this.configuration.sortBy) < 0) {
                this.configuration.sortBy = '';
            }
            found.extraData.options = sortByOptions;
        }
    }

    private getSortByOptions(configuration: TableByGroupWidgetConfiguration): KeyValuePair<string, string>[] {
        let sortByOptions = [].concat(defaultSortBy);
        sortByOptions = sortByOptions.concat(sortByOptionsTableByGroup);
        if (configuration && configuration.series && this.viewModels) {
            const nameConfiguration: NameBuilderConfiguration = {
                hasGroupName: false,
                hasName: true,
                hasValueType: true,
                hasShortComment: false,
                hasUnit: false,
            };
            const indexVm = toDictionary(this.viewModels);
            const sorted = configuration
                .series
                .map(d => d)
                .sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1)
                .map(d => {
                    const vm = indexVm[d.guid];
                    const name = vm ? this._nameBuilderService.getName(vm, nameConfiguration, d as any) : 'Not found';
                    return { key: d.guid + d.value, value: name };
                });
            sortByOptions = sortByOptions.concat(sorted);
        }
        return sortByOptions;
    }
}

export const wtTableByGroupContextual: ComponentDefinition = {
    componentId: 'wtTableByGroupContextual',
    controller: TableByGroupContextualController,
    template: require('./tableByGroupContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        cancel: '&?',
        configuration: '<',
        formController: '=',
        isBindable: '<',
        save: '&?',
        viewModels: '<?'
    }
};